import { useState } from 'react';
import { AlertTriangle, Check, CheckCheck, CircleDot, CornerUpLeft, X } from 'lucide-react';

import { useAuth } from '@/lib/auth/AuthProvider';

import {
  expectedState,
  isStaleRefusal,
  reviewPolicyOf,
  useMarkStorefrontReviewed,
  useRefreshStorefront,
  useReviewStorefront,
  type AdminStorefrontDetail,
  type StorefrontExpectedState,
  type StorefrontReviewAction,
  type StorefrontReviewPolicy,
} from '@/lib/api/storefrontAdminHooks';

import {
  DEFAULT_REVIEW_POLICY,
  ReviewPolicyChooser,
  ReviewPolicySafetyNote,
  reviewPolicyOption,
} from './reviewPolicy';

import { productNameMap } from './storeChanges';
import {
  claimCount,
  collectStoreCopy,
  couponChecks,
  editedSinceApproval as isEditedSinceApproval,
  editsSince,
  lastReviewAt,
  storeMedia,
  storeProblems,
  thinPlans,
} from './storeCompliance';
import { ChangeList } from './storeDiff';
import {
  REVIEW_LABEL,
  REVIEW_TONE,
  canonicalMedicalGroup,
  dateTime,
  isAfter,
  isRealMedicalGroup,
  refusalMessage,
  shortDate,
  usd,
} from './storefrontFormat';
import {
  ActionButton,
  ConfirmDialog,
  Field,
  Notice,
  Panel,
  Pill,
  inputClass,
  textareaClass,
} from './ui';

/** Suggested reviewing medical group for an older backend that sends no
 *  allow-list (the one the house works with today). */
const MEDICAL_GROUP_SUGGESTION = 'WellnessMD';

type Notes = Record<StorefrontReviewAction, string>;
const NO_NOTES: Notes = { approve: '', request_changes: '', reject: '' };

/** The store has never been approved: no approval in its history and no
 *  approved (or backfilled baseline) version. Its first approval sets the
 *  review policy (the backend requires `review_policy` on it). */
export function isFirstApproval(store: AdminStorefrontDetail): boolean {
  if ((store.base_review_status ?? store.review_status) === 'approved') return false;
  if (store.events.some((e) => e.action === 'approved')) return false;
  return !(store.versions ?? []).some((v) => v.kind === 'approved' || v.kind === 'baseline');
}

/** Why Approve is off, in words (the backend would refuse it), or null. */
export function approveBlockReason(store: AdminStorefrontDetail): string | null {
  if (store.review_status === 'changes_pending_review') {
    return 'The store is approved; review the waiting change above instead';
  }
  if (store.review_status === 'approved' && store.status !== 'draft') {
    return store.status === 'paused'
      ? 'Already approved; use Unpause to put it back live'
      : 'Already approved';
  }
  const ownerRun = Boolean(store.owner_email) || store.members.length > 0;
  if (ownerRun && store.review_status === 'draft' && !store.submitted_at) {
    return 'The owner has not submitted the store for review yet';
  }
  if (ownerRun && !store.rules_accepted_at) {
    return 'The owner has not accepted the store rules yet';
  }
  const blocking = storeProblems(store).filter(
    (p) => p.code === 'reserved_slug' || p.code === 'system_name',
  );
  if (blocking.length > 0) {
    return blocking[0].code === 'reserved_slug'
      ? 'Change the reserved address under Store settings first'
      : 'Rename the store under Store settings first (it uses the SYSTEM wordmark)';
  }
  return null;
}

interface Readiness {
  activePlans: number;
  belowFloor: number;
  unknownFloor: number;
}

function readiness(store: AdminStorefrontDetail): Readiness {
  let activePlans = 0;
  let belowFloor = 0;
  let unknownFloor = 0;
  for (const product of store.products) {
    for (const plan of product.plans) {
      if (!plan.active) continue;
      activePlans += 1;
      if (plan.floor_cents == null) unknownFloor += 1;
      else if (plan.price_cents < plan.floor_cents) belowFloor += 1;
    }
  }
  return { activePlans, belowFloor, unknownFloor };
}

/** Review history from the events (newest first): how many times the
 *  owner has submitted, and the last changes request with its note (the
 *  backend clears `review_note` on resubmit, so the event keeps it). */
function reviewRound(store: AdminStorefrontDetail) {
  const submits = store.events.filter((e) => e.action === 'submitted' || e.action === 'resubmitted');
  const lastRequest = store.events.find((e) => e.action === 'changes_requested') ?? null;
  return { round: submits.length, lastRequest };
}

/** The latest note the owner was sent, from a store review (approve,
 *  request changes, reject) or a decision on their changes (approved,
 *  rejected, a revert): `review_note` alone kept an old approval note
 *  after a later change rejection. */
export function latestOwnerNote(
  store: Pick<AdminStorefrontDetail, 'events' | 'review_note' | 'reviewed_at'>,
): { note: string; at: string | null; label: string } | null {
  const REVIEW_LABEL_FOR: Record<string, string> = {
    approved: 'Approval',
    changes_requested: 'Changes request',
    rejected: 'Rejection',
    changes_approved: 'Changes approved',
    changes_rejected: 'Changes rejected',
    reverted: 'Revert',
  };
  // Change decisions are history rows whose note leads with a summary
  // ("Changes not approved: …"); review events carry the note as sent.
  const PREFIX: Record<string, RegExp> = {
    changes_approved: /^Changes approved:\s*/,
    changes_rejected: /^Changes not approved:\s*/,
    reverted: /^Reverted to approved version \d+:\s*/,
  };
  const latest = [...store.events]
    .filter((e) => REVIEW_LABEL_FOR[e.action] && e.note)
    .map((e) => {
      const prefix = PREFIX[e.action];
      const raw = (e.note ?? '').trim();
      // A change decision without a note of its own ("Changes approved").
      if (prefix && !prefix.test(raw)) return null;
      const note = prefix ? raw.replace(prefix, '').trim() : raw;
      return note ? { note, at: e.created_at, label: REVIEW_LABEL_FOR[e.action] } : null;
    })
    .filter((n): n is { note: string; at: string; label: string } => n !== null)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))[0];
  if (latest) return latest;
  return store.review_note ? { note: store.review_note, at: store.reviewed_at, label: 'Review' } : null;
}

/** The store moved on since `snapshot` (another admin, or an owner edit). */
export function movedSince(
  store: AdminStorefrontDetail,
  snapshot: StorefrontExpectedState | null,
): boolean {
  if (!snapshot) return false;
  return (
    store.review_status !== snapshot.expected_review_status ||
    store.status !== snapshot.expected_status ||
    (store.updated_at ?? null) !== snapshot.expected_updated_at
  );
}

export function ReviewPanel({
  store,
  unseenChanges = 0,
}: {
  store: AdminStorefrontDetail;
  /** Changes by someone else the admin has not looked at yet (they are
   *  listed above the panels); actions stay off until they have. */
  unseenChanges?: number;
}) {
  const review = useReviewStorefront(store.id);
  const refresh = useRefreshStorefront(store.id);
  const { session } = useAuth();
  const me = session?.user.email?.toLowerCase() ?? null;
  const [dialog, setDialog] = useState<StorefrontReviewAction | null>(null);
  // The state the admin decided on: sent with the action, so a store that
  // moved on meanwhile is refused instead of overwritten.
  const [snapshot, setSnapshot] = useState<StorefrontExpectedState | null>(null);
  // Notes are kept per action until one is sent, so a refusal (the store
  // changed meanwhile) never discards what the admin typed.
  const [notes, setNotes] = useState<Notes>(NO_NOTES);
  const note = dialog ? notes[dialog] : '';
  const setNote = (value: string) => dialog && setNotes((n) => ({ ...n, [dialog]: value }));
  const groupOptions = store.medical_group_options ?? [];
  const hasRealGroup = isRealMedicalGroup(store.medical_group_name, groupOptions);
  const [group, setGroup] = useState('');
  const [claimsChecked, setClaimsChecked] = useState(false);
  const [copyRead, setCopyRead] = useState(false);
  const [photosChecked, setPhotosChecked] = useState(false);
  const [couponsChecked, setCouponsChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleNotSent, setStaleNotSent] = useState(false);
  // The first approval chooses the review policy; a refusal asking for one
  // (an approval this screen did not take for the first) shows the choice.
  const currentPolicy = reviewPolicyOf(store);
  const [policy, setPolicy] = useState<StorefrontReviewPolicy>(DEFAULT_REVIEW_POLICY);
  const [policyAsked, setPolicyAsked] = useState(false);
  const choosePolicy = isFirstApproval(store) || policyAsked;

  const status = store.review_status;
  // The backend's review baseline (the list uses the same fields), so a
  // change that is only waiting, or edits a change approval, a revert or
  // "mark reviewed" covered, never read as edited since approval.
  const editedSinceApproval = isEditedSinceApproval(store);
  const reviewedAt = lastReviewAt(store);
  const changesSinceReview = editsSince(store, reviewedAt);
  const editCount = store.edits_since_review ?? changesSinceReview.length;
  const lastEditAt = changesSinceReview[changesSinceReview.length - 1]?.created_at ?? null;
  const publishedUnreviewed = store.published_since_review ?? 0;
  const [showAllEdits, setShowAllEdits] = useState(false);
  const markReviewed = useMarkStorefrontReviewed(store.id);
  const [markOpen, setMarkOpen] = useState(false);
  const [markNote, setMarkNote] = useState('');
  const [markError, setMarkError] = useState<string | null>(null);
  const [markSnapshot, setMarkSnapshot] = useState<StorefrontExpectedState | null>(null);
  // Mark reviewed: an approved store (the stored status; a waiting change
  // stays waiting) whose published content has edits nobody reviewed.
  const canMarkReviewed =
    (store.base_review_status ?? store.review_status) === 'approved' &&
    (editedSinceApproval || publishedUnreviewed > 0);
  const ownerNote = latestOwnerNote(store);
  const { zero: zeroPlans, thin: thinPlanList } = thinPlans(store);
  const approveReason = approveBlockReason(store);
  const ready = readiness(store);
  const claims = claimCount(collectStoreCopy(store));
  const problems = storeProblems(store);
  const media = storeMedia(store);
  const newMedia = media.filter((m) => m.newSinceReview);
  const coupons = couponChecks(store);
  const shortCoupons = coupons.filter((c) => c.cappedPlans > 0);
  const { round, lastRequest } = reviewRound(store);
  const resubmitted = status === 'submitted' && Boolean(lastRequest);
  const editedSinceRequest = lastRequest ? isAfter(store.updated_at, lastRequest.created_at) : false;
  const changesSinceRequest = lastRequest ? editsSince(store, lastRequest.created_at) : [];
  const requestByMe =
    !lastRequest?.actor || (me !== null && lastRequest.actor.toLowerCase() === me);
  const requestLabel = requestByMe ? 'Your request' : `Request by ${lastRequest?.actor}`;

  function open(action: StorefrontReviewAction) {
    setGroup(
      hasRealGroup
        ? (canonicalMedicalGroup(store.medical_group_name, groupOptions) ?? store.medical_group_name ?? '')
        : groupOptions.length === 1
          ? groupOptions[0]
          : '',
    );
    setClaimsChecked(false);
    setCopyRead(false);
    setPhotosChecked(false);
    setCouponsChecked(false);
    setError(null);
    setStaleNotSent(false);
    // The stored policy: the column default (Wording & images) on a first
    // approval, and never a silent reset if the history shown is cut short.
    setPolicy(currentPolicy);
    review.reset();
    setSnapshot(expectedState(store));
    setDialog(action);
    // Re-read the store: an owner edit or another admin's decision since
    // this page loaded shows up (and blocks the confirm) before it counts.
    void refresh();
  }
  const moved = dialog !== null && movedSince(store, snapshot);
  const blockedByChanges = unseenChanges > 0 || moved;
  const markMoved =
    markOpen && markSnapshot !== null && (store.updated_at ?? null) !== markSnapshot.expected_updated_at;
  const markBlocked = unseenChanges > 0 || markMoved;

  function openMarkReviewed() {
    setMarkError(null);
    markReviewed.reset();
    setMarkSnapshot(expectedState(store));
    setMarkOpen(true);
    void refresh();
  }

  async function submitMarkReviewed() {
    setMarkError(null);
    try {
      await markReviewed.mutateAsync({
        note: markNote.trim() || null,
        expected_updated_at: (markSnapshot ?? expectedState(store)).expected_updated_at,
      });
      setMarkNote('');
      setMarkOpen(false);
    } catch (err) {
      setMarkError(
        isStaleRefusal(err)
          ? `Not sent. ${refusalMessage(err)} Your note is kept.`
          : refusalMessage(err),
      );
    }
  }

  async function submit() {
    if (!dialog) return;
    setError(null);
    const trimmedGroup = group.trim();
    const groupChanged = trimmedGroup !== (store.medical_group_name ?? '').trim();
    try {
      await review.mutateAsync({
        action: dialog,
        note: note.trim() || null,
        medical_group_name:
          dialog === 'approve' && trimmedGroup && groupChanged ? trimmedGroup : null,
        ...(dialog === 'approve' && choosePolicy ? { review_policy: policy } : {}),
        ...(snapshot ?? expectedState(store)),
      });
      setNotes((n) => ({ ...n, [dialog]: '' }));
      setDialog(null);
    } catch (err) {
      setStaleNotSent(isStaleRefusal(err));
      if ((err as { code?: string } | null)?.code === 'storefront_review_policy_required') {
        setPolicyAsked(true);
      }
      setError(refusalMessage(err));
    }
  }

  const noteRequired = dialog === 'request_changes' || dialog === 'reject';
  const groupRequired = dialog === 'approve' && !hasRealGroup;
  // A chosen group must itself be allowed: one of the backend's groups, or
  // (an older backend) no "[placeholder]".
  const groupInvalid = group.trim() !== '' && !isRealMedicalGroup(group, groupOptions);
  const confirmDisabled =
    blockedByChanges ||
    (noteRequired && !note.trim()) ||
    (dialog === 'approve' &&
      (approveReason !== null ||
        (groupRequired && !isRealMedicalGroup(group, groupOptions)) ||
        groupInvalid ||
        !copyRead ||
        (claims > 0 && !claimsChecked) ||
        (newMedia.length > 0 && !photosChecked) ||
        (shortCoupons.length > 0 && !couponsChecked)));
  const changedNotice = blockedByChanges ? (
    <Notice tone="danger">
      {staleNotSent ? <strong className="font-normal">Not sent. </strong> : null}
      This store changed since you opened it
      {moved && snapshot?.expected_updated_at !== (store.updated_at ?? null)
        ? ' (the owner edited it)'
        : ''}
      . Your note is kept: close this dialog, look over the changes listed at the top of the
      page, then decide again.
    </Notice>
  ) : null;
  const approveBlocked = approveReason !== null;

  return (
    <Panel
      title="Review"
      aside={
        <Pill tone={REVIEW_TONE[status] ?? 'neutral'} dot>
          {resubmitted ? `Resubmitted · round ${Math.max(round, 2)}` : (REVIEW_LABEL[status] ?? status)}
        </Pill>
      }
    >
      <div className="flex flex-col gap-16">
        {status === 'submitted' && !resubmitted && (
          <Notice tone="info">
            The owner submitted this store on {dateTime(store.submitted_at)}. Check the brand,
            copy and prices, then approve it, send it back with a note, or reject it.
          </Notice>
        )}
        {resubmitted && lastRequest && (
          <Notice tone={editedSinceRequest ? 'info' : 'warning'}>
            Resubmitted on {dateTime(store.submitted_at)} after your request of{' '}
            {dateTime(lastRequest.created_at)}.{' '}
            {editedSinceRequest
              ? `The owner last edited the store on ${dateTime(store.updated_at)}. Check that each point below was addressed.`
              : 'The owner has not changed anything since your request.'}
          </Notice>
        )}
        {lastRequest?.note && (status === 'submitted' || status === 'changes_requested') && (
          <div className="flex flex-col gap-6 border-l-2 border-warning pl-12">
            <span className="type-label-xs text-ink-muted normal-case">
              {requestLabel} · {dateTime(lastRequest.created_at)}
            </span>
            <p className="type-body-md text-ink whitespace-pre-wrap leading-130">
              {lastRequest.note}
            </p>
          </div>
        )}
        {resubmitted && changesSinceRequest.length > 0 && (
          <div className="flex flex-col gap-8">
            <span className="type-label-xs text-ink-muted">
              Changed since {requestByMe ? 'your' : 'the'} request
            </span>
            <ChangeList
              net
              productNames={productNameMap(store)}
              changes={changesSinceRequest.map((e) => ({
                field: e.field ?? '',
                old: e.old_value ?? null,
                new: e.new_value ?? null,
              }))}
            />
            <span className="type-body-sm text-ink-muted">
              Changed lines are marked in Patient-facing copy below.
            </span>
          </div>
        )}
        {status === 'changes_requested' && (
          <Notice tone="warning">
            Waiting on the owner. They see your note in their console and can resubmit when it is
            fixed.
          </Notice>
        )}
        {(editedSinceApproval || (canMarkReviewed && publishedUnreviewed > 0)) && (
          <div className="flex flex-col gap-10 border border-warning bg-warning-wash px-12 py-10">
            <span className="type-body-sm text-ink leading-130">
              <strong className="font-normal type-label-xs">Edited since approval</strong>
              {lastEditAt ? ` (last edit ${dateTime(lastEditAt)})` : ''}.{' '}
              {editCount > 0
                ? `${editCount} edit${editCount === 1 ? '' : 's'} since the review on ${dateTime(reviewedAt)}, all published${changesSinceReview.length > 0 ? ':' : '.'}`
                : 'The history has no field changes recorded since the review.'}
              {publishedUnreviewed > 0
                ? ` The owner published ${publishedUnreviewed === 1 ? 'once' : `${publishedUnreviewed} times`} without a review.`
                : ''}
            </span>
            {changesSinceReview.length > 0 && (
              <>
                <ChangeList
                  net
                  productNames={productNameMap(store)}
                  changes={(showAllEdits ? changesSinceReview : changesSinceReview.slice(-6)).map((e) => ({
                    field: e.field ?? '',
                    old: e.old_value ?? null,
                    new: e.new_value ?? null,
                  }))}
                />
                {changesSinceReview.length > 6 && (
                  <button
                    type="button"
                    onClick={() => setShowAllEdits((v) => !v)}
                    className="self-start type-label-xs text-ink underline underline-offset-2"
                  >
                    {showAllEdits ? 'Show the latest 6 edits' : `Show all ${changesSinceReview.length} edits`}
                  </button>
                )}
              </>
            )}
            {canMarkReviewed && (
              <div className="flex flex-wrap items-center gap-10">
                <ActionButton onClick={openMarkReviewed}>
                  <CheckCheck size={14} aria-hidden /> Mark as reviewed
                </ActionButton>
                <span className="type-body-sm text-ink-secondary">
                  Once you have read them: the edits drop out of this list and the store&apos;s
                  &ldquo;Edited since approval&rdquo; marker clears.
                </span>
              </div>
            )}
          </div>
        )}

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-16">
          <Field label="Submitted">{dateTime(store.submitted_at)}</Field>
          <Field label="Rules accepted">{dateTime(store.rules_accepted_at)}</Field>
          <Field label="Reviewed">{dateTime(store.reviewed_at)}</Field>
          <Field label="Reviewed by">{store.reviewed_by || '—'}</Field>
          {!isFirstApproval(store) && (
            <Field label="Review policy" className="col-span-2 md:col-span-4">
              <span className="text-ink">{reviewPolicyOption(currentPolicy).label}</span>
              <span className="text-ink-secondary">
                {' '}
                · {reviewPolicyOption(currentPolicy).explanation}
              </span>
            </Field>
          )}
        </dl>

        {ownerNote && !(lastRequest?.note && (status === 'submitted' || status === 'changes_requested')) && (
          <div className="flex flex-col gap-6 border-l-2 border-line pl-12">
            <span className="type-label-xs text-ink-muted">
              Last note to the owner · {ownerNote.label}
              {ownerNote.at ? ` · ${shortDate(ownerNote.at)}` : ''}
            </span>
            <p className="type-body-md text-ink whitespace-pre-wrap leading-130">
              {ownerNote.note}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-8 pt-4">
          <ActionButton
            tone="primary"
            onClick={() => open('approve')}
            disabled={approveBlocked}
            title={approveReason ?? undefined}
            aria-describedby={approveBlocked ? 'approve-blocked-reason' : undefined}
          >
            <Check size={14} aria-hidden />
            {store.status === 'live' ? 'Approve' : 'Approve and go live'}
          </ActionButton>
          <ActionButton
            onClick={() => open('request_changes')}
            disabled={status === 'changes_requested'}
            title={status === 'changes_requested' ? 'Already waiting on the owner' : undefined}
          >
            <CornerUpLeft size={14} aria-hidden />
            Request changes
          </ActionButton>
          <ActionButton
            tone="danger"
            onClick={() => open('reject')}
            disabled={status === 'rejected'}
            title={status === 'rejected' ? 'Already rejected' : undefined}
          >
            <X size={14} aria-hidden />
            Reject
          </ActionButton>
        </div>
        {approveBlocked && status !== 'approved' && status !== 'changes_pending_review' && (
          <p id="approve-blocked-reason" className="type-body-sm text-ink-muted -mt-8">
            Approve is off: {approveReason?.toLowerCase()}.
          </p>
        )}
      </div>

      <ConfirmDialog
        open={dialog === 'approve'}
        onOpenChange={(next) => !next && setDialog(null)}
        title={store.status === 'live' ? 'Approve store' : 'Approve and go live'}
        description={
          store.status === 'live'
            ? `${store.name} is already live. Approving records the review.`
            : `${store.name} goes live at once: its pages open and it starts taking orders.`
        }
        confirmLabel={store.status === 'live' ? 'Approve' : 'Approve and go live'}
        pending={review.isPending}
        confirmDisabled={confirmDisabled}
        error={blockedByChanges ? null : error}
        onConfirm={submit}
      >
        {changedNotice}
        <ul className="flex flex-col gap-6 type-body-sm">
          {status === 'changes_requested' && lastRequest && (
            <ReadyItem ok={false}>
              {requestByMe ? 'Your' : 'A'} changes request of {dateTime(lastRequest.created_at)} is
              still open and the owner has not resubmitted. Approving closes it as it is.
            </ReadyItem>
          )}
          <ReadyItem ok={ready.activePlans > 0}>
            {ready.activePlans > 0
              ? `${ready.activePlans} active plan${ready.activePlans === 1 ? '' : 's'}`
              : 'No active plan: the store cannot go live'}
          </ReadyItem>
          <ReadyItem ok={ready.belowFloor === 0}>
            {ready.belowFloor === 0
              ? 'Every active plan is at or above its floor'
              : `${ready.belowFloor} plan${ready.belowFloor === 1 ? ' is' : 's are'} below floor`}
          </ReadyItem>
          {ready.unknownFloor > 0 && (
            <ReadyItem ok={false}>
              Floors unavailable for {ready.unknownFloor} plan
              {ready.unknownFloor === 1 ? '' : 's'} (catalog unreachable)
            </ReadyItem>
          )}
          <ReadyItem ok={Boolean(store.rules_accepted_at)}>
            {store.rules_accepted_at ? 'Owner accepted the store rules' : 'Rules not accepted yet'}
          </ReadyItem>
          <ReadyItem ok={null}>
            Automated flags: {claims === 0 ? 'none' : `${claims} (see Patient-facing copy)`}. The
            scan is a keyword aid; read every line yourself.
          </ReadyItem>
          {zeroPlans.length > 0 && (
            <ReadyItem ok={false}>
              {zeroPlans.length === 1
                ? `${zeroPlans[0].productName} · ${zeroPlans[0].planLabel} is priced at floor (${usd(zeroPlans[0].priceCents)}): the store earns $0 on it and no coupon takes anything off it`
                : `${zeroPlans.length} plans are priced at floor: the store earns $0 on them and no coupon takes anything off them`}
            </ReadyItem>
          )}
          {thinPlanList.length > 0 && (
            <ReadyItem ok={false}>
              {thinPlanList.length === 1
                ? `${thinPlanList[0].productName} · ${thinPlanList[0].planLabel} earns the store only ${usd(thinPlanList[0].earningCents)}`
                : `${thinPlanList.length} plans earn the store under 10% over floor`}
            </ReadyItem>
          )}
          <ReadyItem ok={newMedia.length === 0}>
            {media.length === 0
              ? 'Photos: no owner photos or logo (template images only)'
              : newMedia.length === 0
                ? `Photos: ${media.length} owner image${media.length === 1 ? '' : 's'}, none new since the last review`
                : `Photos: ${newMedia.map((m) => m.label.toLowerCase()).join(', ')} new since the last review (see Patient-facing copy)`}
          </ReadyItem>
          <ReadyItem ok={shortCoupons.length === 0}>
            {coupons.length === 0
              ? 'Coupons: none active'
              : shortCoupons.length === 0
                ? 'Coupons: every active coupon delivers what it says'
                : `Coupons: ${shortCoupons.map((c) => c.code).join(', ')} promise${shortCoupons.length === 1 ? 's' : ''} more than the store can fund (see Coupons)`}
          </ReadyItem>
          {problems.map((problem) => (
            <ReadyItem key={problem.code} ok={false}>
              {problem.message}
            </ReadyItem>
          ))}
        </ul>
        {newMedia.length > 0 && (
          <label className="flex items-start gap-8 border border-warning bg-warning-wash px-10 py-8 type-body-sm text-ink">
            <input
              type="checkbox"
              checked={photosChecked}
              onChange={(e) => setPhotosChecked(e.target.checked)}
              className="mt-2"
            />
            <span>
              I looked at the new {newMedia.length === 1 ? 'image' : `${newMedia.length} images`}{' '}
              (Patient-facing copy): no before/after or results imagery, no white coats or
              clinical settings, nothing that suggests a medical endorsement.
            </span>
          </label>
        )}
        {shortCoupons.length > 0 && (
          <label className="flex items-start gap-8 border border-warning bg-warning-wash px-10 py-8 type-body-sm text-ink">
            <input
              type="checkbox"
              checked={couponsChecked}
              onChange={(e) => setCouponsChecked(e.target.checked)}
              className="mt-2"
            />
            <span>
              I checked the coupons: patients get less than the description says on some plans
              (the floor caps the discount). Request changes instead if the owner should fix
              them.
            </span>
          </label>
        )}
        {claims > 0 && (
          <label className="flex items-start gap-8 border border-warning bg-warning-wash px-10 py-8 type-body-sm text-ink">
            <input
              type="checkbox"
              checked={claimsChecked}
              onChange={(e) => setClaimsChecked(e.target.checked)}
              className="mt-2"
            />
            <span>
              I read the {claims === 1 ? 'flagged phrase' : `${claims} flagged phrases`}, and none
              makes a medical or results claim patients could rely on.
            </span>
          </label>
        )}
        <label className="flex items-start gap-8 border border-line bg-raised px-10 py-8 type-body-sm text-ink">
          <input
            type="checkbox"
            checked={copyRead}
            onChange={(e) => setCopyRead(e.target.checked)}
            className="mt-2"
          />
          <span>
            I read every line of the patient-facing copy on this page, including hidden sections,
            and it makes no outcome, safety or endorsement claim.
          </span>
        </label>
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">
            Medical group {groupRequired ? '(required)' : ''}
          </span>
          {groupOptions.length > 0 ? (
            <select
              className={inputClass}
              value={canonicalMedicalGroup(group, groupOptions) ?? ''}
              onChange={(e) => setGroup(e.target.value)}
              autoFocus
            >
              {!canonicalMedicalGroup(group, groupOptions) && (
                <option value="" disabled>
                  Choose the group whose providers review this store
                </option>
              )}
              {groupOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                className={inputClass}
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                placeholder={hasRealGroup ? undefined : 'The group whose providers review this store'}
                maxLength={120}
                autoFocus
              />
              {groupInvalid && (
                <span className="type-body-sm text-warning">
                  Replace the [bracketed] placeholder with the group&apos;s real name.
                </span>
              )}
              {group.trim() !== MEDICAL_GROUP_SUGGESTION && (
                <span className="flex flex-wrap items-center gap-6 type-body-sm text-ink-muted">
                  Suggested:
                  <button
                    type="button"
                    onClick={() => setGroup(MEDICAL_GROUP_SUGGESTION)}
                    className="h-24 px-8 border border-line type-body-sm text-ink hover:bg-sunken"
                  >
                    {MEDICAL_GROUP_SUGGESTION}
                  </button>
                </span>
              )}
            </>
          )}
          {groupOptions.length > 0 && store.medical_group_name && !hasRealGroup && (
            <span className="type-body-sm text-warning">
              The store names “{store.medical_group_name}”, which is not one of the groups we work
              with. Choose one.
            </span>
          )}
          <span className="type-body-sm text-ink-muted leading-130">
            Shown to patients in the store&apos;s disclaimer. Owners cannot change it.
          </span>
        </label>
        {choosePolicy ? (
          <div className="flex flex-col gap-8">
            <ReviewPolicyChooser
              value={policy}
              onChange={setPolicy}
              legend="Review policy (required)"
              hint="Which of the owner's later changes to the live store wait for review. You can change it later from this page."
              name="approve-review-policy"
            />
            <ReviewPolicySafetyNote />
          </div>
        ) : (
          <p className="type-body-sm text-ink-secondary leading-130">
            Review policy: {reviewPolicyOption(currentPolicy).label}.{' '}
            {reviewPolicyOption(currentPolicy).explanation} Change it with Review policy at the top
            of the page.
          </p>
        )}
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Note to the owner (optional)</span>
          <textarea
            className={textareaClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={markOpen}
        onOpenChange={(next) => !next && setMarkOpen(false)}
        title="Mark as reviewed"
        description={`You read what ${store.name} shows patients now. The ${editCount === 1 ? 'edit' : `${editCount} edits`} since the review on ${dateTime(reviewedAt)} drop out of “Edited since approval”.${store.pending_changes ? ' The change waiting for review stays waiting.' : ''} The owner is not notified.`}
        confirmLabel="Mark as reviewed"
        pending={markReviewed.isPending}
        confirmDisabled={markBlocked}
        error={markBlocked ? null : markError}
        onConfirm={submitMarkReviewed}
      >
        {markBlocked && (
          <Notice tone="danger">
            This store changed since you opened it. Your note is kept: close this dialog, look over
            the changes listed at the top of the page, then decide again.
          </Notice>
        )}
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Note (optional, kept in History)</span>
          <textarea
            className={textareaClass}
            value={markNote}
            onChange={(e) => setMarkNote(e.target.value)}
            maxLength={2000}
            autoFocus
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'request_changes'}
        onOpenChange={(next) => !next && setDialog(null)}
        title="Request changes"
        description={
          store.status === 'live'
            ? 'The store stays live while the owner makes the changes.'
            : store.status === 'paused'
              ? 'The store moves from paused to draft until the owner resubmits and you approve it.'
              : 'The store stays a draft until the owner resubmits and you approve it.'
        }
        confirmLabel="Send to owner"
        pending={review.isPending}
        confirmDisabled={confirmDisabled}
        error={blockedByChanges ? null : error}
        onConfirm={submit}
      >
        {changedNotice}
        <NoteField
          value={note}
          onChange={setNote}
          placeholder="What has to change, e.g. “Hero headline makes a results claim; reword it.”"
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'reject'}
        onOpenChange={(next) => !next && setDialog(null)}
        title="Reject store"
        tone="danger"
        description={
          store.status === 'live'
            ? `${store.name} is live: rejecting pauses it, so its pages close and it stops taking orders.`
            : store.status === 'paused'
              ? `${store.name} stays paused and cannot go live unless it is approved later.`
              : `${store.name} stays a draft and cannot go live unless it is approved later.`
        }
        confirmLabel="Reject store"
        pending={review.isPending}
        confirmDisabled={confirmDisabled}
        error={blockedByChanges ? null : error}
        onConfirm={submit}
      >
        {changedNotice}
        <NoteField value={note} onChange={setNote} placeholder="Why the store is rejected" />
      </ConfirmDialog>
    </Panel>
  );
}

/** A checklist line: met, not met, or (null) information to act on, with
 *  no pass or fail (the automated flags are never a clearance). */
function ReadyItem({ ok, children }: { ok: boolean | null; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-8">
      {ok === null ? (
        <CircleDot size={14} className="mt-1 shrink-0 text-ink-muted" aria-hidden />
      ) : ok ? (
        <Check size={14} className="mt-1 shrink-0 text-success" aria-hidden />
      ) : (
        <AlertTriangle size={14} className="mt-1 shrink-0 text-warning" aria-hidden />
      )}
      <span className={ok ? 'text-ink-secondary' : 'text-ink'}>{children}</span>
    </li>
  );
}

function NoteField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="flex flex-col gap-6">
      <span className="type-label-xs text-ink-muted">Note to the owner (required)</span>
      <textarea
        className={textareaClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={2000}
        autoFocus
      />
      <span className="type-body-sm text-ink-muted leading-130">
        The owner sees this in their console. Plain text; no patient details.
      </span>
    </label>
  );
}
